import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMembers, updateMember, deleteMember, useDistricts, syncMemberGroups } from '../../hooks/useMembers';
import { fmtDate, STATUS_BADGE, initials } from '../../lib/constants';
import Modal from '../../components/ui/Modal';
import MemberForm from '../../components/ui/MemberForm';
import FamilyChain from '../../components/ui/FamilyChain';

const SORT_OPTIONS = [
  { value:'name_az',    label:'Name A → Z' },
  { value:'name_za',    label:'Name Z → A' },
  { value:'date_new',   label:'Joined: Newest First' },
  { value:'date_old',   label:'Joined: Oldest First' },
  { value:'created_new',label:'Created: Newest First' },
  { value:'created_old',label:'Created: Oldest First' },
  { value:'dob',        label:'Date of Birth' },
  { value:'district',   label:'District' },
  { value:'number',     label:'Member Number' },
];

function sortMembers(members, sortBy) {
  const arr = [...members];
  switch(sortBy) {
    case 'name_az':    return arr.sort((a,b)=>`${a.first_name}${a.last_name}`.localeCompare(`${b.first_name}${b.last_name}`));
    case 'name_za':    return arr.sort((a,b)=>`${b.first_name}${b.last_name}`.localeCompare(`${a.first_name}${a.last_name}`));
    case 'date_new':   return arr.sort((a,b)=>new Date(b.joined_date||0)-new Date(a.joined_date||0));
    case 'date_old':   return arr.sort((a,b)=>new Date(a.joined_date||0)-new Date(b.joined_date||0));
    case 'created_new':return arr.sort((a,b)=>new Date(b.created_at||0)-new Date(a.created_at||0));
    case 'created_old':return arr.sort((a,b)=>new Date(a.created_at||0)-new Date(b.created_at||0));
    case 'dob':        return arr.sort((a,b)=>new Date(a.dob||0)-new Date(b.dob||0));
    case 'district':   return arr.sort((a,b)=>(a.districts?.name||'').localeCompare(b.districts?.name||''));
    case 'number':     return arr.sort((a,b)=>(a.member_number||'').localeCompare(b.member_number||''));
    default:           return arr;
  }
}

export default function MembersPage({ pendingOnly }) {
  const navigate  = useNavigate();
  const districts = useDistricts();
  const [search,   setSearch]  = useState('');
  const [distId,   setDistId]  = useState('');
  const [mtype,    setMtype]   = useState('');
  const [status,   setStatus]  = useState(pendingOnly ? 'pending' : '');
  const [sortBy,   setSortBy]  = useState('name_az');
  const [viewM,    setViewM]   = useState(null);
  const [editM,    setEditM]   = useState(null);
  const [saving,   setSaving]  = useState(false);

  const { members: rawMembers, loading, refetch } = useMembers({
    search, district_id: distId,
    membership_type: mtype,
    status: status || (pendingOnly ? 'pending' : undefined),
  });

  const members = sortMembers(rawMembers, sortBy);

  async function handleApprove(id) {
    await updateMember(id, { status: 'active' });
    refetch();
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Permanently delete ${name}? This cannot be undone.`)) return;
    await deleteMember(id);
    refetch();
  }

  async function handleEditSave(data, groupIds) {
    setSaving(true);
    const updated = await updateMember(editM.id, data);
    if (updated) {
      await syncMemberGroups(editM.id, groupIds);
      setEditM(null);
      refetch();
    }
    setSaving(false);
  }

  const activeGroups = (m) => (m.member_groups||[]).map(g=>g.groups?.name).filter(Boolean);

  return (
    <div>
      <div className="flex-between mb-4">
        <div>
          <h1 style={{fontSize:20,fontWeight:800}}>{pendingOnly ? '⏳ Pending Approvals' : '👥 All Members'}</h1>
          <div style={{fontSize:13,color:'var(--text3)',marginTop:2}}>
            {pendingOnly ? 'Review and approve new member registrations' : `${members.length} member${members.length!==1?'s':''} found`}
          </div>
        </div>
        <button className="btn btn-primary no-print" onClick={() => navigate('/admin/members/add')}>+ Add Member</button>
      </div>

      {/* Filters + Sort */}
      <div className="card mb-4 no-print">
        <div className="card-pad">
          <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
            <div className="search-bar" style={{flex:'1 1 200px'}}>
              🔍 <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name, phone, email, member no., ID…"/>
              {search&&<button onClick={()=>setSearch('')} style={{background:'none',border:'none',color:'var(--text3)',cursor:'pointer',fontSize:16}}>✕</button>}
            </div>
            <select className="form-select" style={{width:'auto'}} value={distId} onChange={e=>setDistId(e.target.value)}>
              <option value="">All Districts</option>
              {districts.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {!pendingOnly&&(
              <select className="form-select" style={{width:'auto'}} value={status} onChange={e=>setStatus(e.target.value)}>
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="inactive">Inactive</option>
                <option value="deceased">Deceased</option>
              </select>
            )}
            <select className="form-select" style={{width:'auto'}} value={mtype} onChange={e=>setMtype(e.target.value)}>
              <option value="">All Types</option>
              {['Full Member','Associate Member','Adherent','Communicant','Baptised Child'].map(t=><option key={t}>{t}</option>)}
            </select>
            <select className="form-select" style={{width:'auto'}} value={sortBy} onChange={e=>setSortBy(e.target.value)}>
              {SORT_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div style={{fontSize:12,color:'var(--text3)',marginTop:8}}>
            {members.length} member{members.length!==1?'s':''} · Sorted by: <strong>{SORT_OPTIONS.find(o=>o.value===sortBy)?.label}</strong>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{textAlign:'center',padding:40,color:'var(--text3)'}}>Loading members…</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>No.</th>
                <th>Name</th>
                <th>Phone</th>
                <th>District</th>
                <th>Type</th>
                <th>Status</th>
                <th>Joined</th>
                <th className="no-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.length===0&&(
                <tr><td colSpan={8} style={{textAlign:'center',padding:32,color:'var(--text3)'}}>
                  {pendingOnly ? '🎉 No pending members — all approved!' : 'No members found'}
                </td></tr>
              )}
              {members.map(m=>(
                <tr key={m.id} className={m.status==='deceased'?'row-deceased':''}>
                  {/* Member Number */}
                  <td>
                    <span style={{fontFamily:'monospace',fontSize:11,fontWeight:700,color:'var(--blue-dark)',background:'var(--blue-light)',padding:'2px 7px',borderRadius:5,whiteSpace:'nowrap'}}>
                      {m.member_number||'—'}
                    </span>
                  </td>
                  {/* Name */}
                  <td>
                    <div style={{display:'flex',alignItems:'center',gap:9}}>
                      <div style={{width:30,height:30,borderRadius:'50%',background:m.status==='deceased'?'#ECEFF1':'var(--blue-light)',color:m.status==='deceased'?'#90A4AE':'var(--blue-dark)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0}}>
                        {initials(m)}
                      </div>
                      <div>
                        <div style={{fontWeight:700}}>{m.first_name} {m.last_name}</div>
                        <div style={{fontSize:11,color:'var(--text3)'}}>{m.email||''}</div>
                      </div>
                    </div>
                  </td>
                  {/* Phone */}
                  <td style={{fontSize:12,whiteSpace:'nowrap'}}>{m.phone||'—'}</td>
                  {/* District */}
                  <td>
                    {m.districts?.name
                      ? <span style={{fontSize:11,padding:'2px 8px',background:'#EDE7F6',color:'#4527A0',borderRadius:100,fontWeight:600}}>{m.districts.name}</span>
                      : '—'}
                  </td>
                  {/* Type */}
                  <td><span className={`badge ${m.membership_type==='Full Member'?'badge-blue':'badge-gray'}`}>{m.membership_type}</span></td>
                  {/* Status */}
                  <td><span className={`badge badge-${m.status}`}>{m.status}</span></td>
                  {/* Joined */}
                  <td style={{fontSize:12,color:'var(--text3)',whiteSpace:'nowrap'}}>{fmtDate(m.joined_date)}</td>
                  {/* Actions */}
                  <td className="no-print">
                    <div className="flex gap-1" style={{flexWrap:'nowrap'}}>
                      <button className="btn btn-sm btn-blue" onClick={()=>setViewM(m)}>View</button>
                      <button className="btn btn-sm btn-outline" onClick={()=>setEditM({
  id: m.id,
  first_name: m.first_name,
  last_name: m.last_name,
  other_names: m.other_names,
  dob: m.dob,
  gender: m.gender,
  phone_country: m.phone_country || '+254',
  phone: m.phone,
  email: m.email,
  id_number: m.id_number,
  marital_status: m.marital_status,
  district_id: m.district_id,
  family_id: m.family_id,
  family_role: m.family_role,
  membership_type: m.membership_type,
  area: m.area,
  joined_date: m.joined_date,
  is_child: m.is_child,
  is_baptised: m.is_baptised,
  baptism_date: m.baptism_date,
  is_confirmed: m.is_confirmed,
  confirmation_date: m.confirmation_date,
  is_deceased: m.is_deceased,
  death_date: m.death_date,
  is_wedded: m.is_wedded,
  wedding_date: m.wedding_date,
  spouse_name: m.spouse_name,
  selectedGroups: (m.member_groups||[]).map(g=>g.groups?.id).filter(Boolean)
})}>Edit</button>
                      {m.status==='pending'&&<button className="btn btn-sm btn-success" onClick={()=>handleApprove(m.id)}>✅ Approve</button>}
                      <button className="btn btn-sm btn-danger" onClick={()=>handleDelete(m.id,`${m.first_name} ${m.last_name}`)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewM&&(
        <Modal title={`${viewM.first_name} ${viewM.last_name}`} onClose={()=>setViewM(null)} large
          footer={<><button className="btn btn-ghost" onClick={()=>setViewM(null)}>Close</button><button className="btn btn-outline" onClick={()=>{setEditM({
  id: viewM.id,
  first_name: viewM.first_name,
  last_name: viewM.last_name,
  other_names: viewM.other_names,
  dob: viewM.dob,
  gender: viewM.gender,
  phone_country: viewM.phone_country || '+254',
  phone: viewM.phone,
  email: viewM.email,
  id_number: viewM.id_number,
  marital_status: viewM.marital_status,
  district_id: viewM.district_id,
  family_id: viewM.family_id,
  family_role: viewM.family_role,
  membership_type: viewM.membership_type,
  area: viewM.area,
  joined_date: viewM.joined_date,
  is_child: viewM.is_child,
  is_baptised: viewM.is_baptised,
  baptism_date: viewM.baptism_date,
  is_confirmed: viewM.is_confirmed,
  confirmation_date: viewM.confirmation_date,
  is_deceased: viewM.is_deceased,
  death_date: viewM.death_date,
  is_wedded: viewM.is_wedded,
  wedding_date: viewM.wedding_date,
  spouse_name: viewM.spouse_name,
  selectedGroups: (viewM.member_groups||[]).map(g=>g.groups?.id).filter(Boolean)
});setViewM(null);}}>✏️ Edit</button></>}>
          {/* Member Number badge */}
          {viewM.member_number&&(
            <div style={{background:'linear-gradient(135deg,var(--blue-dark),var(--blue))',borderRadius:10,padding:'10px 16px',marginBottom:16,display:'flex',alignItems:'center',justifyContent:'space-between',color:'white'}}>
              <div><div style={{fontSize:10,opacity:.7,letterSpacing:.8,textTransform:'uppercase'}}>Member Number</div><div style={{fontSize:20,fontWeight:900,letterSpacing:2}}>{viewM.member_number}</div></div>
              <div style={{fontSize:24,opacity:.3}}>✝</div>
            </div>
          )}
          <div className="grid-2 mb-4">
            {[
              ['District',viewM.districts?.name||'—'],
              ['Membership',viewM.membership_type],
              ['Status',viewM.status],
              ['Gender',viewM.gender||'—'],
              ['Marital Status',viewM.marital_status||'—'],
              ['Year of Birth', viewM.birth_year || '—'],
              ['Phone',viewM.phone||'—'],
              ['Email',viewM.email||'—'],
              ['National ID',viewM.id_number||'—'],
              ['Area',viewM.area||'—'],
              ['Family',viewM.families?.name||'—'],
              ['Family Role',viewM.family_role||'—'],
              ['Joined',fmtDate(viewM.joined_date)],
              ['Baptism Date',fmtDate(viewM.baptism_date)],
              ['Confirmation',fmtDate(viewM.confirmation_date)],
            ].map(([l,v])=>(
              <div key={l}><div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',marginBottom:3}}>{l}</div><div style={{fontWeight:600}}>{v}</div></div>
            ))}
          </div>
          {/* Life event flags */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {viewM.is_baptised  && <span className="badge badge-blue">💧 Baptised</span>}
            {viewM.is_confirmed && <span className="badge badge-blue">✝️ Confirmed</span>}
            {viewM.is_wedded    && <span className="badge badge-amber" style={{background:'#FFF8E0',color:'#C47A00'}}>💍 Wedded</span>}
            {viewM.is_deceased  && <span className="badge badge-gray">⚰️ Deceased</span>}
          </div>
          {/* Groups */}
          <div className="mb-4">
            <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',marginBottom:8}}>Groups</div>
            <div className="flex flex-wrap gap-2">{activeGroups(viewM).length?activeGroups(viewM).map(g=><span key={g} className="badge badge-blue">✝ {g}</span>):<span style={{color:'var(--text3)',fontSize:13}}>No groups</span>}</div>
          </div>
          {viewM.family_id&&<FamilyChain familyId={viewM.family_id} familyName={viewM.families?.name} currentMemberId={viewM.id}/>}
        </Modal>
      )}

      {/* EDIT MODAL */}
      {editM&&(
        <Modal title={`Edit — ${editM.first_name} ${editM.last_name}`} onClose={()=>setEditM(null)} large>
          <MemberForm initial={editM} onSubmit={handleEditSave} loading={saving} submitLabel="Save Changes"/>
        </Modal>
      )}
    </div>
  );
}
